/**
 * Where to send a user when an init route answers 401.
 *
 * An idle timeout must END the session, not restart it. Every sign-in entry point in
 * this app re-enters on the still-valid Cognito hosted-UI session cookie — no
 * credentials, no prompt — and the callback then writes a fresh activity stamp. So
 * bouncing an idle-timeout 401 to sign-in silently resets the very 30-minute CJIS
 * policy that 401 was enforcing: no error, no sign-out, just a user who is never
 * signed out. Only a sign-out ends the hosted-UI session.
 *
 * `scope=local` clears BOTH pools (app/api/sign-out/route.ts), so a CSM's customer
 * cookies go with their employee ones, and it leaves the user's other devices alone.
 *
 * This is a shared decision because the mistake was not a one-off: TR-3393 review found
 * the same missing check in three independent 401 handlers — `refreshAuthSession`,
 * `loadAppInit`, and the `/employee/app` shell. A new init caller should route its 401
 * through here instead of re-deriving the rule and getting it wrong a fourth time.
 */

import { IDLE_TIMEOUT_HEADER } from '@/lib/session/constants';

/** Terminal destination for an idle-expired session. */
export const IDLE_SIGN_OUT_HREF = '/api/sign-out?scope=local';

/** True when this response is an init route reporting an idle timeout. */
export function isIdleTimeout(response: Pick<Response, 'headers'>): boolean {
    return response.headers.get(IDLE_TIMEOUT_HEADER) === 'true';
}

/**
 * Destination for a 401 from an init route: sign-out when it is an idle timeout,
 * otherwise the caller's own sign-in page (a dead refresh token is not an idle
 * timeout, and sign-in is the correct destination for it).
 */
export function logoutHrefFor401(
    response: Pick<Response, 'headers'>,
    signInHref: string,
): string {
    return isIdleTimeout(response) ? IDLE_SIGN_OUT_HREF : signInHref;
}
